// src/config/centralAuth.ts
// Central Identity configuration for the web client
//
// This file is a thin app-specific wrapper around @levelup/central-auth.
// It reads env vars, sets APP_ID, and re-exports shared helpers
// pre-bound with CRM's configuration.

import {
  buildOidcEndpoints,
  buildBearerEndpoints,
  buildSwitchContextUrl,
  buildOidcLogoutUrl as _buildOidcLogoutUrl,
  initiateCentralLogin as _initiateCentralLogin,
  initiateCentralLogout as _initiateCentralLogout,
  PKCE_VERIFIER_KEY,
  OAUTH_STATE_KEY,
  OAUTH_RETURN_PATH_KEY,
} from '@levelup/central-auth';

import type { AuthMode } from '@levelup/central-auth';

// Re-export shared constants for backward compatibility
export { PKCE_VERIFIER_KEY, OAUTH_STATE_KEY, OAUTH_RETURN_PATH_KEY };

// ============================================================
// Testing Mode
// ============================================================

/**
 * Header name for forwarding the testing mode override role to the backend.
 *
 * PRESENTATION ONLY — this header is used by the backend exclusively for
 * role-sensitive presentation logic (e.g. menu template selection).
 * It is NOT used for authorization, permission checks, or security decisions.
 *
 * Central does not re-issue JWT tokens when testing mode is activated,
 * so the token's role claim always carries the real role. This header
 * bridges that gap for presentation endpoints.
 */
export const TESTING_MODE_ROLE_HEADER = 'X-Testing-Mode-Role';

// Re-export type
export type { AuthMode };

// ============================================================
// App-specific Configuration (reads env vars)
// ============================================================

/**
 * Central URL from environment.
 */
export const CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL || '';

/**
 * Authentication mode.
 */
export const AUTH_MODE: AuthMode = (() => {
  const mode = import.meta.env.VITE_AUTH_MODE as AuthMode | undefined;
  if (mode && ['central', 'legacy', 'hybrid'].includes(mode)) {
    return mode;
  }
  return import.meta.env.PROD ? 'central' : 'hybrid';
})();

/**
 * Feature flag to enable Central auth UI components.
 */
export const CENTRAL_AUTH_ENABLED = (() => {
  const enabled = import.meta.env.VITE_CENTRAL_AUTH_ENABLED;
  if (enabled === 'false' || enabled === '0') return false;
  if (enabled === 'true' || enabled === '1') return true;
  return AUTH_MODE !== 'legacy';
})();

/**
 * H-2C2 WP-6 — frontend gate for the OIDC RP (BFF-model) boot path.
 *
 * When ON, `AppBootGate` mounts `RpBootGate` (consumes the server-only
 * `GET /auth/context`) instead of the legacy Bearer-model `BootGate`. Default
 * OFF — this flag is NOT set in this PR.
 *
 * Deploy-mismatch behavior (the two flags are independent):
 *  - frontend ON + backend `HANDOFF_OIDC_CRM` OFF → the flag-gated `/auth`
 *    router answers 503 `handoff_oidc_crm_disabled`; `RpBootGate` surfaces
 *    this as `auth_unavailable` — a configuration/availability surface,
 *    deliberately NOT `degraded` (which would wrongly imply a Central
 *    outage). It does NOT fall back to the legacy boot path.
 *  - frontend OFF + backend ON → the legacy `BootGate` runs; the dormant
 *    `/auth` routes are simply never consumed by the browser.
 * Keep the two flags flipped together; ON-frontend before ON-backend only
 * during a brief, recovery-surfaced deploy window.
 */
export const HANDOFF_OIDC_CRM_ENABLED = (() => {
  const enabled = import.meta.env.VITE_HANDOFF_OIDC_CRM;
  return enabled === 'true' || enabled === '1';
})();

/**
 * App identifier for Central suite integration.
 */
export const APP_ID = 'crm';

/**
 * Suite-shell `fallbackUrl` for SuiteShellAdapter.
 *
 * Consumed by `@levelup/ui-shell` (>=1.2.5) `useAppAccessGuard` only when:
 *   - apps[] is non-empty (so the guard runs), AND
 *   - currentAppId (`crm`) is absent from apps[], AND
 *   - no user-facing apps are available to redirect into.
 *
 * In practice this branch should never fire when /auth/context returns a
 * sensible apps[] (today's traces show `home` plus 4+ facing apps for both
 * business and personal workspaces). The fallback exists so the guard NEVER
 * returns `decision: 'block'` — which would render the shell's redirect
 * placeholder with no destination, stranding the user.
 *
 * Resolution order:
 *   1. `VITE_HOME_URL` if set at build time (lets ops override per env without
 *      a code change).
 *   2. Host-rewrite of `window.location.origin`:
 *        crm-staging.roilevelup.com → home-staging.roilevelup.com
 *        crm.roilevelup.com         → home.roilevelup.com
 *      Matches the staging/prod host pattern observed in
 *      `/auth/context` apps[] payloads.
 *   3. Empty string (only reachable in SSR / older test envs where `window`
 *      is undefined — in those contexts the adapter is never mounted anyway).
 */
export const SUITE_HOME_FALLBACK_URL: string = (() => {
  const fromEnv = import.meta.env.VITE_HOME_URL as string | undefined;
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined') {
    return window.location.origin.replace(/\/\/crm(-|\.)/, '//home$1');
  }
  return '';
})();

/**
 * Storage key prefix for Central auth tokens.
 */
export const STORAGE_KEY_PREFIX = 'levelup_central';

// ============================================================
// Pre-built Endpoints (from shared builders + CENTRAL_URL)
// ============================================================

export const OIDC_ENDPOINTS = buildOidcEndpoints(CENTRAL_URL);
export const BEARER_ENDPOINTS = buildBearerEndpoints(CENTRAL_URL);
export const SUITE_SWITCH_CONTEXT_URL = buildSwitchContextUrl(CENTRAL_URL);

// ============================================================
// App-specific Helpers
// ============================================================

/**
 * Check if Central auth is configured and available.
 */
export function isCentralAuthConfigured(): boolean {
  return CENTRAL_AUTH_ENABLED && Boolean(CENTRAL_URL);
}

/**
 * Check if legacy auth should be shown.
 */
export function showLegacyAuth(): boolean {
  return AUTH_MODE === 'legacy' || AUTH_MODE === 'hybrid';
}

/**
 * Check if Central auth should be shown.
 */
export function showCentralAuth(): boolean {
  return (AUTH_MODE === 'central' || AUTH_MODE === 'hybrid') && isCentralAuthConfigured();
}

// ============================================================
// CRM-bound Login/Logout (delegates to shared with CRM config)
// ============================================================

/**
 * Initiate the Central OIDC Authorization Code + PKCE flow.
 */
export async function initiateCentralLogin(returnPath: string = '/'): Promise<void> {
  const redirectUri = `${window.location.origin}/auth/callback`;

  return _initiateCentralLogin({
    centralUrl: CENTRAL_URL,
    clientId: APP_ID,
    redirectUri,
    scope: 'openid profile email',
    returnPath,
  });
}

/**
 * Build the OIDC RP-Initiated Logout URL.
 */
export function buildOidcLogoutUrl(params: {
  centralUrl?: string;
  clientId?: string;
  postLogoutRedirectUri: string;
}): string {
  return _buildOidcLogoutUrl({
    centralUrl: params.centralUrl || CENTRAL_URL,
    clientId: params.clientId || APP_ID,
    postLogoutRedirectUri: params.postLogoutRedirectUri,
  });
}

/**
 * Initiate Central OIDC RP-Initiated Logout.
 *
 * RP/BFF mode (`HANDOFF_OIDC_CRM_ENABLED`): the app owns its own server-side RP
 * session, so it must destroy that first and TREAT IT AS A HARD GATE. Only after
 * the local RP session is confirmed destroyed (`crm_sid` cleared) do we destroy
 * the Central SSO session and redirect through Central's OIDC end-session
 * endpoint (built with the canonical `buildOidcLogoutUrl` helper).
 *
 * TRULY fail-closed: if the local RP logout does NOT succeed, this THROWS and
 * does nothing else — it does not kill Central SSO and does not redirect. That
 * prevents the ambiguous "Central SSO destroyed but `crm_sid` still valid" state
 * (user believes they signed out while the RP session is live). The caller stays
 * on the app, the session is fully intact, and the user can retry; the rejection
 * is surfaced through the app's existing global error-tracking path.
 *
 * Legacy Bearer mode: unchanged — there is no `crm_sid` server session; the
 * shared `_initiateCentralLogout` clears client storage and redirects.
 */
export async function initiateCentralLogout(postLogoutRedirectUri?: string): Promise<void> {
  const redirectUri = postLogoutRedirectUri || `${window.location.origin}/login`;

  if (HANDOFF_OIDC_CRM_ENABLED) {
    // RP/BFF — GATE: destroy the app-owned RP session + clear crm_sid FIRST. If
    // this does not succeed, ABORT the whole logout (throw) BEFORE touching
    // Central SSO or redirecting, so we never fail *open*.
    let localLogout: Response;
    try {
      localLogout = await fetch('/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Cache-Control': 'no-store' },
      });
    } catch {
      throw new Error(
        'RP session logout request failed; aborting logout to avoid an ambiguous signed-out state',
      );
    }
    if (!localLogout.ok) {
      throw new Error(
        `RP session logout failed (${localLogout.status}); aborting logout to avoid an ambiguous signed-out state`,
      );
    }

    // Local RP session confirmed destroyed — now it is safe to complete the
    // Central-side logout. Clear OAuth flow transients, destroy the Central SSO
    // session, then redirect via the canonical end-session URL.
    try {
      localStorage.removeItem(PKCE_VERIFIER_KEY);
      localStorage.removeItem(OAUTH_STATE_KEY);
      localStorage.removeItem(OAUTH_RETURN_PATH_KEY);
    } catch {
      // localStorage may be unavailable in some embed/test contexts — non-fatal.
    }
    try {
      await fetch(`${CENTRAL_URL}/api/oidc/session/logout`, {
        method: 'POST',
        credentials: 'include', // Send Central's session cookies
      });
    } catch {
      // Best-effort — the end-session redirect below also destroys Central SSO.
    }
    window.location.assign(
      buildOidcLogoutUrl({ centralUrl: CENTRAL_URL, clientId: APP_ID, postLogoutRedirectUri: redirectUri }),
    );
    return;
  }

  // Legacy Bearer: unchanged — destroy Central SSO, then the shared helper clears
  // client storage and redirects. There is no crm_sid server session in this mode.
  try {
    await fetch(`${CENTRAL_URL}/api/oidc/session/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // Best-effort — continue with client-side cleanup even if this fails
  }
  _initiateCentralLogout({
    centralUrl: CENTRAL_URL,
    clientId: APP_ID,
    postLogoutRedirectUri: redirectUri,
    storageKeyPrefix: STORAGE_KEY_PREFIX,
  });
}
